import { Drawer, Form, Select, DatePicker, InputNumber, Button, Space, Row, Col, Divider, Typography } from 'antd';
import { FilterOutlined, ClearOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { useEffect } from 'react';
import { t } from '../../i18n';

const { RangePicker } = DatePicker;
const { Text } = Typography;

export interface UserFilterValues {
  search?: string;
  phone?: string;
  role?: string;
  status?: string;
  isDriverVerified?: boolean;
  minTrips?: number;
  maxTrips?: number;
  minRating?: number;
  maxRating?: number;
  registeredFrom?: string;
  registeredTo?: string;
  hasVehicle?: boolean;
  blockedOnly?: boolean;
  hasCompletedTrips?: boolean;
}

interface UserFiltersProps {
  open: boolean;
  onClose: () => void;
  onApply: (values: UserFilterValues) => void;
  onReset: () => void;
  filterType: 'all' | 'passengers' | 'drivers';
  initialValues?: UserFilterValues;
}

export const UserFilters = ({
  open,
  onClose,
  onApply,
  onReset,
  filterType,
  initialValues,
}: UserFiltersProps) => {
  const [form] = Form.useForm();

  // Sync the form with the page's current filter state every time the
  // drawer opens. Ant Design's `initialValues` only applies on first mount,
  // so without this the form would show stale values when reopened.
  useEffect(() => {
    if (!open) return;
    form.resetFields();
    const v: any = { ...(initialValues || {}) };
    if (initialValues?.registeredFrom && initialValues?.registeredTo) {
      v.dateRange = [
        dayjs(initialValues.registeredFrom),
        dayjs(initialValues.registeredTo),
      ];
    }
    form.setFieldsValue(v);
  }, [open, initialValues, form]);

  const handleApply = () => {
    const values = form.getFieldsValue();
    const dateRange = values.dateRange;

    const filters: UserFilterValues = {
      ...values,
      registeredFrom: dateRange?.[0]?.format('YYYY-MM-DD'),
      registeredTo: dateRange?.[1]?.format('YYYY-MM-DD'),
    };
    delete (filters as any).dateRange;

    // Remove empty values
    Object.keys(filters).forEach((key) => {
      if (filters[key as keyof UserFilterValues] === undefined ||
          filters[key as keyof UserFilterValues] === null ||
          filters[key as keyof UserFilterValues] === '') {
        delete filters[key as keyof UserFilterValues];
      }
    });

    onApply(filters);
    onClose();
  };

  const handleReset = () => {
    // Force-clear every field so resetFields() doesn't restore stale
    // initialValues — those only apply on first mount.
    form.setFieldsValue({
      isDriverVerified: undefined,
      minTrips: undefined,
      maxTrips: undefined,
      hasCompletedTrips: undefined,
      minRating: undefined,
      maxRating: undefined,
      dateRange: undefined,
      blockedOnly: undefined,
      hasVehicle: undefined,
    });
    onReset();
    onClose();
  };

  return (
    <Drawer
      title={
        <Space>
          <FilterOutlined />
          <span>{t('filters.advancedFilter')}</span>
        </Space>
      }
      placement="right"
      width={420}
      onClose={onClose}
      open={open}
    >
      {/* Action buttons below header */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 24 }}>
        <Button icon={<ClearOutlined />} onClick={handleReset} style={{ flex: 1 }}>
          Tozalash
        </Button>
        <Button type="primary" onClick={handleApply} style={{ flex: 1 }}>
          Qo'llash
        </Button>
      </div>

      <Divider style={{ marginTop: 0 }} />

      <Form
        form={form}
        layout="vertical"
        initialValues={initialValues}
      >
        {/* Verification Status - for 'all' and 'drivers' */}
        {(filterType === 'all' || filterType === 'drivers') && (
          <>
            <Text strong style={{ display: 'block', marginBottom: 16 }}>{t('filters.verificationStatus')}</Text>
            <Form.Item label={t('filters.driverVerified')} name="isDriverVerified">
              <Select
                allowClear
                placeholder="Barchasi"
                options={[
                  { value: true, label: 'Tasdiqlangan' },
                  { value: false, label: 'Tasdiqlanmagan' },
                ]}
              />
            </Form.Item>
            <Divider />
          </>
        )}

        {/* Trip Statistics */}
        <Text strong style={{ display: 'block', marginBottom: 16 }}>{t('filters.tripStatistics')}</Text>

        <div style={{ marginBottom: 16 }}>
          <div style={{ marginBottom: 8 }}>{t('filters.tripCount')}</div>
          <Row gutter={8}>
            <Col span={12}>
              <Form.Item name="minTrips" noStyle>
                <InputNumber
                  placeholder={t('filters.minTrips')}
                  min={0}
                  style={{ width: '100%' }}
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="maxTrips" noStyle>
                <InputNumber
                  placeholder={t('filters.maxTrips')}
                  min={0}
                  style={{ width: '100%' }}
                />
              </Form.Item>
            </Col>
          </Row>
        </div>

        <Form.Item label={t('filters.hasCompletedTrips')} name="hasCompletedTrips">
          <Select
            allowClear
            placeholder="Barchasi"
            options={[
              { value: true, label: t('filters.yesCompleted') },
              { value: false, label: t('filters.noCompleted') },
            ]}
          />
        </Form.Item>

        {/* Rating range - for 'all' and 'drivers' */}
        {(filterType === 'all' || filterType === 'drivers') && (
          <>
            <Divider />
            <Text strong style={{ display: 'block', marginBottom: 16 }}>{t('table.rating')}</Text>
            <div style={{ marginBottom: 16 }}>
              <Row gutter={8}>
                <Col span={12}>
                  <Form.Item name="minRating" noStyle>
                    <InputNumber
                      placeholder={t('filters.minRating')}
                      min={1}
                      max={5}
                      step={0.1}
                      style={{ width: '100%' }}
                    />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item name="maxRating" noStyle>
                    <InputNumber
                      placeholder={t('filters.maxRating')}
                      min={1}
                      max={5}
                      step={0.1}
                      style={{ width: '100%' }}
                    />
                  </Form.Item>
                </Col>
              </Row>
            </div>
          </>
        )}

        <Divider />

        {/* Registration date range */}
        <Text strong style={{ display: 'block', marginBottom: 16 }}>{t('filters.registrationDate')}</Text>
        <Form.Item name="dateRange">
          <RangePicker
            style={{ width: '100%' }}
            placeholder={[t('filters.from'), t('filters.to')]}
            format="YYYY-MM-DD"
          />
        </Form.Item>

        {/* Blocked filter */}
        <Form.Item label={t('filters.blockedUsers')} name="blockedOnly">
          <Select
            allowClear
            placeholder="Barchasi"
            options={[
              { value: true, label: t('filters.onlyBlocked') },
              { value: false, label: t('filters.onlyNotBlocked') },
            ]}
          />
        </Form.Item>

        {/* Has vehicle - for drivers only */}
        {filterType === 'drivers' && (
          <>
            <Divider />
            <Text strong style={{ display: 'block', marginBottom: 16 }}>{t('filters.vehicleStatus')}</Text>
            <Form.Item label={t('filters.vehicleStatus')} name="hasVehicle">
              <Select
                allowClear
                placeholder="Barchasi"
                options={[
                  { value: true, label: t('filters.hasVehicle') },
                  { value: false, label: t('filters.noVehicle') },
                ]}
              />
            </Form.Item>
          </>
        )}
      </Form>
    </Drawer>
  );
};
